import { scopeKind, securityItem } from './searchModel';
import type { SearchItem, SearchScope } from './searchModel';

const scopeForKind: Record<SearchItem['kind'], SearchScope> = {
  command: 'functions',
  client: 'clients',
  strategy: 'strategies',
  security: 'securities',
};
export const emptyFilters = { country: 'All', type: 'All', exchange: 'All' };
export type InstrumentFilters = typeof emptyFilters;
const categoryOrder = ['command', 'client', 'strategy', 'security'] as const;
const frequentCodes = new Set(['ORD', 'TRD', 'REBAL']);

export type SearchGroup = {
  key: string;
  label: string;
  items: Array<SearchItem>;
  scope?: SearchScope;
  total?: number;
};
type ResultGroupsInput = {
  query: string;
  scope: SearchScope;
  ranked: Array<SearchItem>;
  items: Array<SearchItem>;
  favorites: Array<SearchItem>;
  recents: Array<SearchItem>;
  labels: {
    best: string;
    favorites: string;
    recent: string;
    frequent: string;
    categories: Record<SearchItem['kind'], string>;
  };
};

/** One definition of the compact All limits, shared by pointer and keyboard results. */
export function buildResultGroups({
  query,
  scope,
  ranked,
  items,
  favorites,
  recents,
  labels,
}: ResultGroupsInput): Array<SearchGroup> {
  if (!query.trim()) {
    const favoriteKeys = new Set(favorites.map((item) => item.key));
    const recentKeys = new Set(recents.map((item) => item.key));
    return [
      { key: 'favorites', label: labels.favorites, items: favorites },
      {
        key: 'recent',
        label: labels.recent,
        items: recents
          .filter((item) => !favoriteKeys.has(item.key))
          .slice(0, 5),
      },
      {
        key: 'frequent',
        label: labels.frequent,
        items: items.filter(
          (item) =>
            item.kind === 'command' &&
            frequentCodes.has(item.code) &&
            !favoriteKeys.has(item.key) &&
            !recentKeys.has(item.key),
        ),
      },
    ]
      .map((group) => ({
        ...group,
        items:
          scope === 'all'
            ? group.items
            : group.items.filter((item) => item.kind === scopeKind[scope]),
      }))
      .filter((group) => group.items.length > 0);
  }
  if (scope !== 'all')
    return [
      {
        key: scope,
        label: labels.categories[scopeKind[scope]],
        items: ranked,
        total: ranked.length,
      },
    ];
  const best = ranked.at(0);
  return [
    ...(best ? [{ key: 'best', label: labels.best, items: [best] }] : []),
    ...categoryOrder.map((kind) => ({
      key: kind,
      label: labels.categories[kind],
      items: ranked
        .slice(1)
        .filter((item) => item.kind === kind)
        .slice(0, 3),
      scope: scopeForKind[kind],
      total: ranked.filter((item) => item.kind === kind).length,
    })),
  ];
}

/** Expanded entries use their own conid so opening a child never selects the parent. */
export function flattenResultGroups(
  groups: Array<SearchGroup>,
  expanded: Set<string>,
) {
  return groups.flatMap((group) =>
    group.items.flatMap((item) =>
      item.kind === 'security' && expanded.has(item.key)
        ? [item, ...item.children.map((child) => securityItem(child, [], true))]
        : [item],
    ),
  );
}
